import React, { useState, useEffect } from 'react';
import { Task, TaskPriority } from '../../types/task';
import { Button } from '../ui/button';
import { PriorityBadge } from './PriorityBadge';
import { TaskForm } from './TaskForm';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { cn } from '../../utils/cn';
import { 
  CheckCircleIcon,
  TrashIcon,
  PencilIcon,
  ArrowsUpDownIcon,
  PlusIcon,
  RocketLaunchIcon,
  ListBulletIcon,
  ClockIcon,
  ArrowLeftIcon,
  CalendarIcon,
  CheckIcon,
  FolderIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { showToast } from '@/utils/toast';
import { motion } from 'framer-motion';
import { SearchBar } from '@/components/ui/SearchBar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SearchFilters } from '@/components/ui/SearchBar';
import { DndKitWrapper, SortableItem } from '../ui/dnd-kit-wrapper';
import { GripVertical } from 'lucide-react';
import { useProjects } from '../../contexts/ProjectContext';

type TaskWithStringPriority = Omit<Task, 'priority'> & {
  priority: 'low' | 'medium' | 'high';
};



export const TaskList: React.FC = () => {
  const context = useProjects();
  const { projects, tasks: contextTasks, addTask, updateTask, deleteTask, loading } = context;
  const error = (context as any).error;
  
  // Use tasks from context instead of local state - memoize to prevent infinite loops
  const tasks = React.useMemo(() => 
    contextTasks.map((task: any) => ({
      ...task,
      id: String(task.id),
      completed: task.status === 'completed',
      tags: task.tags || [],
      priority: (task.priority || 'medium').toLowerCase() as 'low' | 'medium' | 'high',
    })), [contextTasks]
  );

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'pomodoros' | 'priority'>('date');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [filterType, setFilterType] = useState<'active' | 'completed' | 'all'>('all');
  const [focusedTaskId] = useState<string | null>(null);
 
  const [filteredTasks, setFilteredTasks] = useState<TaskWithStringPriority[]>(tasks);

  useEffect(() => {
    const filtered = tasks
      .filter((task: Task) => {
        if (filterType === 'active' && task.completed) {
          return false;
        }
        if (filterType === 'completed' && !task.completed) {
          return false;
        }
        
        if (searchFilters.priority && task.priority !== searchFilters.priority) {
          return false;
        }
        
        if (searchQuery.trim()) {
          const search = searchQuery.toLowerCase();
          return (
            task.title.toLowerCase().includes(search) ||
            task.description?.toLowerCase().includes(search) ||
            (task.tags || []).some((tag: string) => tag.toLowerCase().includes(search)) ||
            task.priority?.toLowerCase().includes(search)
          );
        }
        
        return true;
      });

    setFilteredTasks(filtered);
  }, [tasks, filterType, searchFilters, searchQuery]);

  const handleCreateTask = async (taskData: any) => {
    try {
      await addTask({
        ...taskData,
        completed: false,
        completedPomodoros: 0,
        projectId: taskData.projectId || '',
        createdAt: Date.now(),
      });
      
      setShowTaskForm(false);
      showToast.success('Task created successfully');
    } catch (error) {
      console.error('Error creating task:', error);
      showToast.error('Failed to create task');
    }
  };


  const handleComplete = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    try {
      await updateTask(taskId, {
        status: task.completed ? 'pending' : 'completed'
      } as any);
      showToast.success(task.completed ? 'Task marked as incomplete' : 'Task completed');
    } catch (error) {
      console.error('Error updating task:', error);
      showToast.error('Failed to update task');
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteTask(taskToDelete.id);
      showToast.success('Task deleted successfully');
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      showToast.error('Failed to delete task');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (taskData: Partial<TaskWithStringPriority>) => {
    try {
      if (editingTask) {
        // Update existing task
        await updateTask(editingTask.id, {
          title: taskData.title,
          description: taskData.description,
          projectId: taskData.projectId || editingTask.projectId || null,
          priority: (taskData.priority || 'medium') as 'low' | 'medium' | 'high',
          estimatedPomodoros: taskData.estimatedPomodoros,
        } as any);
        showToast.success('Task updated successfully');
      } else {
        // Create new task
        await addTask({
          title: taskData.title || '',
          description: taskData.description || '',
          projectId: taskData.projectId || null,
          priority: (taskData.priority || 'medium') as 'low' | 'medium' | 'high',
          estimatedPomodoros: taskData.estimatedPomodoros || 1,
        } as any);
        showToast.success('Task created successfully');
      }
      setShowTaskForm(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      showToast.error('Failed to save task');
    }
  };

  const handleSearch = (query: string, filters?: SearchFilters) => {
    setSearchQuery(query);
    if (filters) {
      setSearchFilters(filters);
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || '';
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!focusedTaskId || ['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        return;
      }

      const task = tasks.find(t => t.id === focusedTaskId);
      if (!task) return;

      switch (e.key.toLowerCase()) {
        case 'e':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleSetEditingTask(task);
            setShowTaskForm(true);
          }
          break;
        case 'space':
          e.preventDefault();
          handleComplete(task.id);
          break;
        case 'delete':
        case 'backspace':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleSetTaskToDelete(task);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [focusedTaskId, tasks]);

  const handleDragEnd = (reorderedTasks: any[]) => {
    // Update the filtered tasks for display
    setFilteredTasks(reorderedTasks.filter(task => {
      if (filterType === 'active' && task.completed) {
        return false;
      }
      if (filterType === 'completed' && !task.completed) {
        return false;
      }
      
      if (searchFilters.priority && task.priority !== searchFilters.priority) {
        return false;
      }
      
      if (searchQuery.trim()) {
        const search = searchQuery.toLowerCase();
        return (
          task.title.toLowerCase().includes(search) ||
          task.description?.toLowerCase().includes(search) ||
          (task.tags || []).some((tag: string) => tag.toLowerCase().includes(search)) ||
          task.priority?.toLowerCase().includes(search)
        );
      }
      
      return true;
    }));

    showToast.success('Task order updated');
  };

  if (tasks.length === 0) {
    return (
      <motion.div 
        className="text-center space-y-8 py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            delay: 0.2, 
            duration: 0.5,
            type: "spring",
            stiffness: 200,
            damping: 15
          }}
          className="flex justify-center"
        >
          <div className="relative">
            {/* Animated background rings */}
            <div className="absolute inset-0 animate-ping-slow opacity-30">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full" />
            </div>
            <div className="absolute inset-0 animate-pulse-slow opacity-40">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full" />
            </div>
            
            {/* Main icon container */}
            <motion.div 
              className="relative bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full p-8 backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RocketLaunchIcon className="h-16 w-16 text-blue-500 animate-float" />
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="space-y-3"
        >
          <motion.h3 
            className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            Start Your Journey
          </motion.h3>
          <motion.p 
            className="text-lg text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            Create a task and track your progress
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          {showTaskForm ? (
            <motion.div 
              className="max-w-md mx-auto"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <TaskForm
                  onSubmit={handleCreateTask}
                  onCancel={() => setShowTaskForm(false)}
                  showBackButton={false}
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Button
                onClick={() => setShowTaskForm(true)}
                size="lg"
                className="gap-2 px-8 py-6 text-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 group-hover:scale-110" />
                <div className="relative flex items-center gap-2">
                  <PlusIcon className="h-6 w-6" />
                  <span>Create First Task</span>
                </div>
              </Button>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    );
  }

  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;

  const handleSetEditingTask = (task: Task) => {
    setEditingTask({
      ...task,
      tags: task.tags || [],
    });
  };

  const handleSetTaskToDelete = (task: Task) => {
    setTaskToDelete({
      ...task,
      tags: task.tags || [],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" data-testid="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <p className="text-lg font-medium text-red-500">Failed to load tasks</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Tasks
            </h2>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">
              <span className="text-sm font-medium text-primary">
                {completedCount}/{totalCount}
              </span>
              <span className="text-xs text-primary/70">completed</span>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1">
            <SearchBar 
              onSearch={handleSearch}
              placeholder="Search tasks..."
              className="w-full"
              priorities={['low', 'medium', 'high', 'urgent']}
            />
          </div>

          <div className="flex gap-2 lg:w-auto">
            {/* Active/Completed Filter */}
            <DropdownMenu>
              <TooltipProvider >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="default"
                        className="flex-1 lg:flex-none justify-between min-w-[130px] bg-background/50"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-primary" />
                          <span>
                            {filterType === 'active' ? 'Active' : 
                             filterType === 'completed' ? 'Completed' : 
                             'All Tasks'}
                          </span>
                        </div>
                        <ChevronDownIcon className="h-4 w-4 ml-1.5 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Filter tasks by completion status</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuItem 
                  onClick={() => {
                    setFilterType('active');
                  }}
                  className="gap-2"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Active Tasks
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setFilterType('completed');
                  }}
                  className="gap-2"
                >
                  <CheckIcon className="h-4 w-4" />
                  Completed Tasks
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setFilterType('all');
                  }}
                  className="gap-2"
                >
                  <ListBulletIcon className="h-4 w-4" />
                  All Tasks
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Due Date Filter */}
            <DropdownMenu>
              <TooltipProvider >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="default"
                        className="flex-1 lg:flex-none justify-between min-w-[130px] bg-background/50"
                      >
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4 text-primary" />
                          <span>
                            {sortBy === 'date' ? 'Due Date' : 
                             sortBy === 'pomodoros' ? 'Time' : 
                             'Priority'}
                          </span>
                        </div>
                        <ChevronDownIcon className="h-4 w-4 ml-1.5 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Sort tasks by {sortBy === 'date' ? 'due date' : 
                                     sortBy === 'pomodoros' ? 'estimated time' : 
                                     'priority level'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuItem onClick={() => setSortBy('date')}>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Due Date
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('pomodoros')}>
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Time
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('priority')}>
                  <ArrowsUpDownIcon className="h-4 w-4 mr-2" />
                  Priority
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* New Task Button */}
            <Button
              onClick={() => setShowTaskForm(true)}
              className="shrink-0 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
              size="default"
              aria-label="Add Task"
            >
              <PlusIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">New Task</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Task List with Drag and Drop */}
      <DndKitWrapper
        items={filteredTasks}
        onDragEnd={handleDragEnd}
        className="space-y-3"
      >
        {filteredTasks.map((task: Task) => (
          <SortableItem
            key={String(task.id)}
            id={String(task.id)}
            className={cn(
              "group relative p-5 rounded-xl border transition-colors duration-200",
              "bg-white dark:bg-slate-900/50",
              "hover:border-primary/30",
              "dark:hover:bg-slate-800/80",
              task.completed && "bg-zinc-50 dark:bg-slate-900/30",
              "dark:border-slate-800",
              focusedTaskId === task.id && "ring-2 ring-primary/50"
            )}
          >
                      {/* Priority Indicator */}
                      <div 
                        className={cn(
                          "absolute left-0 top-0 bottom-0 w-1.5 rounded-l-xl transition-all duration-300",
                          task.priority === 'high' ? "bg-gradient-to-b from-red-400 to-red-600" :
                          task.priority === 'medium' ? "bg-gradient-to-b from-yellow-400 to-yellow-600" :
                          task.priority === 'low' ? "bg-gradient-to-b from-green-400 to-green-600" : 
                          "bg-gradient-to-b from-gray-400 to-gray-600",
                          "opacity-50 group-hover:opacity-80 group-hover:w-2"
                        )}
                      />

                      <div className="relative flex items-start gap-4 pl-2">
                        {/* Check/Uncheck Button - Keep on left but adjust position */}
                        <div className="absolute left-2 top-1/2 -translate-y-1/2">
                          <TooltipProvider >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleComplete(task.id)}
                                  className={cn(
                                    "h-6 w-6 p-0 rounded-full transition-all duration-200",
                                    task.completed
                                      ? "bg-primary hover:bg-primary border-transparent"
                                      : "bg-white hover:border-primary border-gray-200",
                                    "border-2 flex items-center justify-center"
                                  )}
                                >
                                  <motion.div
                                    initial={false}
                                    animate={{ 
                                      scale: task.completed ? 1 : 0,
                                      opacity: task.completed ? 1 : 0
                                    }}
                                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                  >
                                    <CheckIcon className="h-3.5 w-3.5 text-white stroke-[3]" />
                                  </motion.div>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{task.completed ? 'Mark as incomplete' : 'Mark as complete'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        {/* Content - Add left padding to account for check button */}
                        <div className="flex-1 min-w-0 space-y-3 pl-10">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className={cn(
                                  "font-medium text-base truncate max-w-[280px] sm:max-w-[400px]",
                                  task.completed && "text-muted-foreground/70 line-through"
                                )}>
                                  {task.title}
                                </h4>
                                <div className="flex items-center gap-1.5">
                                  {task.priority && (
                                    <PriorityBadge priority={task.priority as TaskPriority} />
                                  )}
                                  {task.recurring && (
                                    <span className={cn(
                                      "inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md flex-shrink-0 transition-all duration-200",
                                      task.recurring === 'daily'
                                        ? "border-rose-500 bg-rose-50/80 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800"
                                        : task.recurring === 'weekly'
                                        ? "border-emerald-500 bg-emerald-50/80 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800"
                                        : "border-amber-500 bg-amber-50/80 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800",
                                      "border"
                                    )}>
                                      <ArrowPathIcon className="h-3 w-3 animate-spin-slow" />
                                      <span className="font-medium">{task.recurring}</span>
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Project */}
                              {task.projectId && (
                                <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                                  <FolderIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span className="truncate font-medium">{getProjectName(task.projectId)}</span>
                                </div>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-start gap-1.5 pt-1">
                              <div className={cn(
                                "flex items-center gap-1.5",
                                "sm:opacity-0 sm:group-hover:opacity-100 sm:transition-all sm:duration-200",
                                "sm:translate-x-4 sm:group-hover:translate-x-0"
                              )}>
                                {/* Drag Handle */}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <div className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-muted/50 transition-colors cursor-grab active:cursor-grabbing">
                                          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                                        </div>
                                      </motion.div>
                                    </TooltipTrigger>
                                    <TooltipContent>Drag to reorder</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>

                                {/* Edit Button */}
                                <TooltipProvider >
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            handleSetEditingTask(task);
                                            setShowTaskForm(true);
                                          }}
                                          className="h-7 w-7 p-0 hover:bg-primary hover:text-white"
                                        >
                                          <PencilIcon className="h-3.5 w-3.5" />
                                        </Button>
                                      </motion.div>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit task</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>

                                {/* Delete Button */}
                                <TooltipProvider >
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleSetTaskToDelete(task)}
                                          className="h-7 w-7 p-0 hover:bg-red-500 hover:text-white"
                                        >
                                          <TrashIcon className="h-3.5 w-3.5" />
                                        </Button>
                                      </motion.div>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete task</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          {task.description && (
                            <p className="text-sm text-zinc-600 dark:text-slate-300 line-clamp-2 break-words">
                              {task.description}
                            </p>
                          )}

                          {/* Progress bar */}
                          {task.estimatedPomodoros > 0 && (
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                                <ClockIcon className="h-4 w-4 text-primary/70" />
                                <span>
                                  {task.completedPomodoros === 0 
                                    ? `${task.estimatedPomodoros} pomodoros planned`
                                    : task.completedPomodoros === task.estimatedPomodoros
                                      ? 'All pomodoros completed!'
                                      : `${task.completedPomodoros} of ${task.estimatedPomodoros} pomodoros done`
                                  }
                                </span>
                              </div>
                              <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800/50 rounded-full overflow-hidden">
                                <div 
                                  className={cn(
                                    "h-full rounded-full transition-all duration-300",
                                    task.completed 
                                      ? "bg-gradient-to-r from-green-400/90 to-green-500/90" 
                                      : "bg-gradient-to-r from-blue-500/80 to-blue-600/80"
                                  )}
                                  style={{ 
                                    width: `${(task.completedPomodoros / task.estimatedPomodoros) * 100}%` 
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Tags & Due Date */}
                          {((task.tags?.length ?? 0) > 0 || task.dueDate) && (
                            <div className="flex flex-wrap items-center gap-1.5 pt-1">
                              {task.tags?.map((tag: string, index: number) => (
                                <div
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                >
                                  <span className="opacity-70">#</span>
                                  <span className="truncate ml-0.5">{tag}</span>
                                </div>
                              ))}
                              {task.dueDate && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted/30 text-muted-foreground/60">
                                  <CalendarIcon className="h-3 w-3" />
                                  {new Date(task.dueDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                      </div>
          </SortableItem>
        ))}
      </DndKitWrapper>

      {/* Task Form Modal */}
      {showTaskForm && (
        <div className="fixed inset-0 bg-black/50 z-[100] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl max-h-[90vh] rounded-xl border bg-white dark:bg-zinc-900 overflow-hidden"
            >
              <div className="p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-6">
                  <button
                    onClick={() => {
                      setShowTaskForm(false);
                      setEditingTask(null);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                  <h2 className="text-xl font-semibold">
                    {editingTask ? 'Edit Task' : 'Create New Task'}
                  </h2>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                  <TaskForm
                    onSubmit={handleSubmit}
                    onCancel={() => {
                      setShowTaskForm(false);
                      setEditingTask(null);
                    }}
                    initialData={editingTask}
                    showBackButton={false}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};